import { DocumentIcon } from '@nexus/ari-lib/assets/icons';
import { imageOutlineSx } from '@nexus/ari-lib/theme/surfaces';
import { RecentFilesCard } from '@nexus/ari-lib/sections/file-library';

import { Box, Typography } from '@mui/material';

import type { DocumentType } from '../documents/documents-data';
import { DocumentPreviewImage } from '../documents/documents-preview-image';

// ----------------------------------------------------------------------

export type DocumentItem = {
  id: string;
  label: string;
  type: DocumentType;
  /** Page 1 of a document, or the picture itself. Without one the tile shows the file type. */
  previewUrl?: string | null;
};

type DashboardDocumentsSectionProps = {
  documents: DocumentItem[];
  loading?: boolean;
  error?: Error | null;
  onClick?: () => void;
  /** Opens one document. Without it a tile opens Documents, like the rest of the card. */
  onOpenDocument?: (id: string) => void;
};

// Two rows of three fill the card. A name gets two lines, so most names show whole.
const COLUMNS = 3;
const PREVIEW_HEIGHT = 70;

export function DashboardDocumentsSection({
  documents,
  loading = false,
  error = null,
  onClick,
  onOpenDocument,
}: DashboardDocumentsSectionProps) {
  return (
    <RecentFilesCard
      testId="cloud.dashboard.documents-section"
      title="Documents"
      subtitle="Knowledge Base"
      icon={<DocumentIcon sx={{ width: 28, height: 28 }} />}
      onClick={onClick}
      items={documents}
      loading={loading}
      emptyIcon={<DocumentIcon sx={{ width: 40, height: 40, opacity: 0.4 }} />}
      emptyLabel={error ? 'Could not load your documents.' : 'No documents uploaded yet'}
      columns={COLUMNS}
      gap={1.25}
      renderItem={(doc) => (
        <Box
          title={doc.label}
          data-testid={`cloud.dashboard.document.${doc.id}`}
          {...(onOpenDocument && {
            role: 'link',
            tabIndex: 0,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              onOpenDocument(doc.id);
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key !== 'Enter') return;
              e.stopPropagation();
              onOpenDocument(doc.id);
            },
          })}
          sx={{
            minWidth: 0,
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: 'container.medium',
            cursor: 'pointer',
            transition: 'background-color 120ms ease-out',
            '&:hover': { bgcolor: 'container.high' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          <DocumentPreviewImage
            src={doc.previewUrl ?? null}
            alt=""
            type={doc.type}
            glyphSize={22}
            sx={(theme) => ({
              // Not display: block, which would pull the fallback glyph into the corner.
              verticalAlign: 'top',
              width: '100%',
              height: PREVIEW_HEIGHT,
              objectFit: 'cover',
              objectPosition: 'top',
              ...imageOutlineSx(theme),
            })}
          />
          {/* The padding sits outside the clamp, or a third line shows in it. */}
          <Box sx={{ px: 1, py: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.3,
                // Two lines in every tile, so the rows stay even.
                height: '2.6em',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {doc.label}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
}
